import { createApi } from "@reduxjs/toolkit/query/react";
import { ListUsersDto } from "@s2z-platform/dto";
import { ListResponse } from "@s2z-platform/types";
import {
  EstimateEntity,
  ProductEntity,
  UserEntity,
} from "@s2z-platform/entity";
import {
  EstimateStatusEnum,
  EvidenceFeatureEnum,
} from "@s2z-platform/constants";
import { buildBaseQuery } from "./functions";

export const userApi = createApi({
  reducerPath: "userApi",
  baseQuery: buildBaseQuery(),
  tagTypes: ["USERS", "CURRENT_USER"],
  endpoints: (builder) => ({
    getProducts: builder.query<ProductEntity[], null>({
      query: () => "/api/admin/products",
    }),
    getUsers: builder.query<ListResponse<UserEntity>, ListUsersDto>({
      query: (dto) => ({
        url: "/api/admin/users",
        params: {
          ...dto,
          productIdentifiers:
            dto.productIdentifiers && dto.productIdentifiers.length > 0
              ? dto.productIdentifiers?.join(",")
              : undefined,
          storePlatforms:
            dto.storePlatforms && dto.storePlatforms.length > 0
              ? dto.storePlatforms?.join(",")
              : undefined,
          searchQuery:
            dto.searchQuery?.length && dto.searchQuery?.length > 0
              ? dto.searchQuery
              : undefined,
        },
      }),
      providesTags: ["USERS"],
    }),
    getUserInfo: builder.query<UserEntity, string>({
      query: (uuid) => ({
        url: `/api/admin/users/${uuid}`,
      }),
      providesTags: ["USERS", "CURRENT_USER"],
    }),
    updateEstimateStatus: builder.mutation<
      EstimateEntity,
      {
        accountUuid: string;
        estimateUuid: string;
        estimateStatus: EstimateStatusEnum;
      }
    >({
      query: ({ accountUuid, estimateUuid, estimateStatus }) => ({
        method: "PATCH",
        url: `/api/admin/accounts/${accountUuid}/estimates/${estimateUuid}/status`,
        body: {
          estimateStatus,
        },
      }),
      invalidatesTags: ["CURRENT_USER"],
    }),
    getEvidenceEntrySignedUrl: builder.query<
      { signedUrl: string },
      {
        accountUuid: string;
        feature: EvidenceFeatureEnum;
        evidenceUuid: string;
      }
    >({
      query: ({ accountUuid, feature, evidenceUuid }) => ({
        method: "GET",
        url: `/api/admin/accounts/${accountUuid}/evidence/${feature}/${evidenceUuid}`,
      }),
    }),
    getSqidAlphabet: builder.query<{ sqidAlphabet: string }, void>({
      query: () => ({
        method: "GET",
        url: "/api/admin/sqid-alphabet",
      }),
    }),
  }),
});

export const {
  useGetProductsQuery,
  useGetUsersQuery,
  useGetUserInfoQuery,
  useUpdateEstimateStatusMutation,
  useLazyGetEvidenceEntrySignedUrlQuery,
  useGetSqidAlphabetQuery,
} = userApi;
